import type { Cart, CartLine, AddToCartPayload, UpdateCartPayload } from './core';
import { CartService } from './cart-service';
import { createLogger } from '@/lib/logger';

/**
 * Resolve a line item in `cart` from a key that may have gone stale.
 *
 * Shopify hashes a line's properties into its key, so any property write (the
 * line item properties update chain does this whenever a promotion label is
 * added or cleaned up) replaces the key while the line itself is unchanged.
 * DOM held over from before that write points at a key the API no longer
 * accepts — `400 no valid id or line parameter`.
 *
 * Recovery is by variant id, which is the stable half of the key, and only
 * when it identifies exactly one line. Two lines of the same variant (e.g. a
 * purchased line plus a free gift) are ambiguous, so those resolve to
 * `undefined` and the caller re-renders rather than guessing.
 *
 * @param cart Authoritative cart to resolve against.
 * @param key Line item key, possibly stale.
 * @returns The matching line, or `undefined` when it cannot be resolved.
 */
export function resolveLineItem(cart: Cart, key: string): CartLine | undefined {
  const exact = cart.items.find((i) => i.key === key);
  if (exact) return exact;

  const variantId = key.split(':')[0];
  if (!variantId) return undefined;

  const candidates = cart.items.filter((i) => (i.key ?? '').split(':')[0] === variantId);
  return candidates.length === 1 ? candidates[0] : undefined;
}

export class StaleLineItemKeyError extends Error {
  constructor(public readonly key: string) {
    super(`Stale line item key: ${key}`);
    this.name = 'StaleLineItemKeyError';
  }
}

export class MissingLineItemKeyError extends Error {
  constructor() {
    super('Missing line item key');
    this.name = 'MissingLineItemKeyError';
  }
}

export type AddItem = {
  id?: number; // variant id
  variantId?: number; // alias
  quantity?: number;
  properties?: Record<string, any> | null;
};

/**
 * Convenience wrapper over `CartService` for common imperative operations.
 *
 * Provides terse helpers like `addVariant`, `setQuantityByKey`, and `updateNote`
 * while delegating to the underlying `CartService` for network calls and events.
 */
export class SimpleCartService {
  private cartService: CartService;
  private logger = createLogger('simple-cart-service');

  /**
   * @param svc The underlying `CartService` instance.
   */
  constructor(svc: CartService) {
    this.cartService = svc;
  }

  // Add a single variant by id
  /**
   * Add a single variant to the cart.
   * @param variantId Variant ID to add.
   * @param quantity Quantity to add (default 1).
   * @param properties Optional line item properties.
   * @returns Updated cart.
   */
  async addVariant(
    variantId: number,
    quantity: number = 1,
    properties?: Record<string, any> | null,
  ): Promise<Cart> {
    this.logger.info('Adding variant', variantId, quantity, properties);
    const payload: AddToCartPayload = {
      items: [{ id: variantId, quantity, properties }],
    };
    return this.cartService.runExclusive(() => this.cartService.add(payload));
  }

  // Add multiple items at once
  /**
   * Add multiple items to the cart.
   * @param items Items to add. Accepts `id` or `variantId`.
   * @returns Updated cart.
   */
  async addItems(items: AddItem[]): Promise<Cart> {
    this.logger.info('Adding items', items);
    const payload: AddToCartPayload = {
      items: items.map((i) => ({
        id: i.id ?? i.variantId!,
        quantity: i.quantity ?? 1,
        properties: i.properties ?? undefined,
      })),
    } as any;
    return this.cartService.runExclusive(() => this.cartService.add(payload));
  }

  // Set quantity by line item key (preferred)
  /**
   * Set quantity for a line item addressed by `key`.
   * @param key Line item key.
   * @param quantity Desired quantity.
   * @param properties Optional properties.
   * @returns Updated cart.
   */
  async setQuantityByKey(
    key: string,
    quantity: number,
    properties?: Record<string, any> | null,
  ): Promise<Cart> {
    this.logger.info('Setting quantity by key', key, quantity, properties);
    return this.mutateLineByKey(key, () => quantity, properties);
  }

  /**
   * Read the current cart and mutate one line, holding the mutation queue for
   * the whole read-modify-write.
   *
   * Everything that addresses a line by key goes through here: resolving the
   * key and writing the change inside one queued task is what stops a
   * concurrent write from rekeying the line between the two.
   *
   * @param key Line item key, possibly stale.
   * @param resolveQuantity Given the resolved line, the quantity to write.
   * @param properties Properties to write, or `undefined` to leave them alone.
   * @returns Updated cart.
   */
  private mutateLineByKey(
    key: string,
    resolveQuantity: (line: CartLine) => number,
    properties?: Record<string, any> | null,
  ): Promise<Cart> {
    if (!key || typeof key !== 'string') {
      return Promise.reject(new MissingLineItemKeyError());
    }

    return this.cartService.runExclusive(async () => {
      const cart = await this.cartService.get();
      const line = resolveLineItem(cart, key);

      if (!line || !line.key) {
        this.logger.warn('Unresolvable line item key, forcing re-render', { key });
        this.cartService.notifyChanged(cart, 'change');
        throw new StaleLineItemKeyError(key);
      }

      if (line.key !== key) {
        this.logger.info('Recovered stale line item key', { staleKey: key, currentKey: line.key });
      }

      return this.cartService.change({
        id: line.key,
        quantity: resolveQuantity(line),
        properties,
      });
    });
  }

  /**
   * Increment quantity for a line item by `by` (default 1).
   *
   * `by` is applied as a delta against the quantity read inside the queued
   * task, so two increments never both read the same starting quantity and
   * write the same result.
   *
   * Throws `StaleLineItemKeyError` when the key cannot be resolved even by
   * variant id, having first emitted `Changed` to force a re-render.
   *
   * @param key Line item key.
   * @param by Increment step (can be negative).
   * @returns Updated cart.
   */
  async incrementByKey(key: string, by: number = 1): Promise<Cart> {
    this.logger.info('Incrementing quantity by key', key, by);
    return this.mutateLineByKey(key, (line) => Math.max(0, line.quantity + by));
  }

  /**
   * Decrement quantity for a line item by `by` (default 1).
   * @param key Line item key.
   * @param by Decrement step.
   * @returns Updated cart.
   */
  async decrementByKey(key: string, by: number = 1): Promise<Cart> {
    return this.incrementByKey(key, -by);
  }

  /**
   * Remove a line item by setting quantity to 0.
   * @param key Line item key.
   * @returns Updated cart.
   */
  async removeByKey(key: string): Promise<Cart> {
    this.logger.info('Removing by key', key);
    return this.mutateLineByKey(key, () => 0);
  }

  /**
   * Replace a line's properties while preserving whatever quantity the cart
   * currently holds.
   *
   * Used by the line item properties update chain. The chain proposes from a
   * cart snapshot, so writing the snapshot's quantity back would revert a
   * quantity change the user made in the meantime — reading the quantity
   * inside the queued task avoids that.
   *
   * @param key Line item key, possibly stale.
   * @param properties Properties to write.
   * @returns Updated cart.
   */
  async setPropertiesByKey(key: string, properties: Record<string, any>): Promise<Cart> {
    this.logger.info('Setting properties by key', key, properties);
    return this.mutateLineByKey(key, (line) => line.quantity, properties);
  }

  // Set quantity by 1-indexed line position
  /**
   * Set quantity for a line item by 1-indexed line position.
   * @param line 1-indexed line position.
   * @param quantity Desired quantity.
   * @param properties Optional properties.
   * @returns Updated cart.
   */
  async setQuantityByLine(
    line: number,
    quantity: number,
    properties?: Record<string, any> | null,
  ): Promise<Cart> {
    this.logger.info('Setting quantity by line', line, quantity, properties);
    return this.cartService.runExclusive(() =>
      this.cartService.change({ line, quantity, properties }),
    );
  }

  /**
   * Remove a line item by position by setting quantity to 0.
   * @param line 1-indexed line position.
   * @returns Updated cart.
   */
  async removeByLine(line: number): Promise<Cart> {
    this.logger.info('Removing by line', line);
    return this.setQuantityByLine(line, 0);
  }

  /**
   * Update the cart note.
   * @param note New note value, or null to clear.
   * @returns Updated cart.
   */
  async updateNote(note: string | null): Promise<Cart> {
    this.logger.info('Updating note', note);
    const payload: UpdateCartPayload = { note };
    return this.cartService.runExclusive(() => this.cartService.update(payload));
  }

  /**
   * Update cart attributes.
   * @param attributes Key/value attributes (null to remove a key).
   * @returns Updated cart.
   */
  async updateAttributes(attributes: Record<string, string | null>): Promise<Cart> {
    this.logger.info('Updating attributes', attributes);
    const payload: UpdateCartPayload = { attributes };
    return this.cartService.runExclusive(() => this.cartService.update(payload));
  }

  async updateItems(items: UpdateCartPayload): Promise<Cart> {
    this.logger.info('Updating items', items);
    return this.cartService.runExclusive(() => this.cartService.update(items));
  }

  /**
   * Clear the cart.
   * @returns Updated (empty) cart.
   */
  async clear(): Promise<Cart> {
    this.logger.info('Clearing cart');
    return this.cartService.runExclusive(() => this.cartService.clear());
  }

  /**
   * Get the current cart.
   * @returns Current cart.
   */
  async get(): Promise<Cart> {
    return this.cartService.get();
  }
}
